












































import {Component, Prop, Vue} from 'vue-property-decorator';
import ItemLabelCarousel from '@/components/item-label/ItemLabelCarousel.vue';
import ShadowBox from '@/components/ShadowBox.vue';
import type {IItemBoxItem} from '@/components/item-box/IItemBoxItem';
import type {TItemLabelName} from '@customer-portal/shared';

type TAlignOptionsHorizontal = 'left' | 'center' | 'right';
type TAlignOptionsVertical = 'top' | 'middle' | 'bottom';

export enum EItemBoxImageFormat {
	SQUARE_SMALL = 'square',
	SQUARE_LARGE = 'square large',
}

/**
 * component for consistent display of Items
 *
 * By default, the item has a single flex-row with a single content area, .item-main.
 * This area is populated with the given IItemBoxItem props, which may include
 * <TItemLabelName>s for use in an ItemLabelCarousel.
 *
 * You may provide additional content for `left` and `right` slots within .item-main.
 * By default, their content is centered vertically, but this can be configured by passing
 * `'top'` or `'bottom'` to `valignLeft` or `valignRight`, respectively.
 *
 * You may specify content for an `actions` slot that appears below the main
 * content. This is an area for item controls, such as Favorite or Delete. By default,
 * these are right-aligned (flex-end), but can be configured by passing `'left'` or
 * `'center'` to `alignFooter`.
 *
 * Finally, there is a `'drawer'` slot to hold content whose display can be toggled
 * with the `showDrawer` prop (default: `false`). When triggered, the ItemBox's height
 * will expand to show the new content, and the box will have an `.expanded` style
 * applied.
 *
 * Specifying the `no-shadow` Boolean prop renders the item in a DIV instead of
 * in a ShadowBox component. The `color` prop allows you to choose preset styling.
 *
 * To avoid additional spacing from flex gaps or margins applied to empty DIVs, slots
 * are rendered conditionally.
 */
@Component({
	components: {
		ItemLabelCarousel,
		ShadowBox,
	}
})
export default class ItemBox extends Vue {
	@Prop({default: 'right'})
	alignFooter!: TAlignOptionsHorizontal;

	@Prop({default: EItemBoxImageFormat.SQUARE_LARGE})
	imageFormat!: EItemBoxImageFormat;

	@Prop({default: false})
	isSelected!: boolean;

	@Prop({required: true})
	item!: IItemBoxItem;

	@Prop({type: Boolean})
	noShadow!: boolean;

	@Prop({default: 'middle'})
	valignLeft!: TAlignOptionsVertical;

	@Prop({default: 'top'})
	valignMain!: TAlignOptionsVertical;

	@Prop({default: 'middle'})
	valignRight!: TAlignOptionsVertical;

	@Prop({default: false})
	showDrawer!: boolean;

	@Prop({default: 'transparent'})
	color!: 'transparent' | 'white';

	@Prop({type: Boolean})
	noninteractive!: boolean;

	@Prop({type: Boolean})
	disable!: boolean;

	drawerHeight = 0;
	drawerElement: Element | null = null;
	drawerResizeObserver: ResizeObserver | null = null;

	recalculateDrawerHeight() {
		if(this.drawerElement) {
			this.drawerHeight = this.drawerElement.scrollHeight;
		}
	}

	get cssVariables() {
		return {
			'--drawer-height': `${this.drawerHeight}px`,
		};
	}

	get labelList(): TItemLabelName[] {
		return this.item.labels ?? ([] as TItemLabelName[]);
	}

	get hasNameOnly() {
		return !this.item?.description && !this.item?.subtitle;
	}

	mounted() {
		if(this.$slots.drawer) {
			this.drawerElement = this.$el.querySelector('.item-drawer');
			if(this.drawerElement) {
				this.drawerResizeObserver = new ResizeObserver(() => {
					this.recalculateDrawerHeight();
				});

				// This is the critical part: We observe the size of all children!
				for(const child of this.drawerElement.children) {
					this.drawerResizeObserver.observe(child);
				}
			}
			this.recalculateDrawerHeight();
		}
	}

	destroyed() {
		if(this.drawerResizeObserver) {
			this.drawerResizeObserver.disconnect();
		}
	}
}
